import axiosInstance from '../lib/api/axiosInstance';
import { 
  Domain,
  ServiceSubscription,
  CreateServiceSubscriptionRequest,
  UpdateServiceSubscriptionRequest
} from '../types';

// Type pour la réponse des domaines legacy Adblock
interface AdblockLegacyDomainsResponse {
  domains: Domain[];
  count: number;
}

/**
 * Récupère tous les domaines
 */
export const getAllDomains = async (filters?: {
  networkId?: number;
  search?: string;
}): Promise<Domain[]> => {
  try {
    const response = await axiosInstance.get<Domain[]>('/domains', { params: filters });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des domaines:', error);
    throw error;
  }
};

/**
 * Récupère un domaine par ID
 */
export const getDomainById = async (id: number): Promise<Domain> => {
  try {
    const response = await axiosInstance.get<Domain>(`/domains/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de la récupération du domaine ${id}:`, error);
    throw error;
  }
};

/**
 * Récupère les domaines pour les sélecteurs d'abonnement
 */
export const getDomainsForSubscription = async (): Promise<Domain[]> => {
  try {
    const response = await axiosInstance.get<Domain[]>('/domains/for-subscription');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des domaines pour abonnement:', error);
    throw error;
  }
};

/**
 * Récupère tous les services disponibles
 */
export const getAllServices = async (): Promise<ServiceSubscription[]> => {
  try {
    const response = await axiosInstance.get<ServiceSubscription[]>('/services');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des services:', error);
    throw error;
  }
};

/**
 * Récupère tous les abonnements aux services
 */
export const getAllServiceSubscriptions = async (filters?: {
  domainId?: number;
  serviceId?: number;
  status?: string;
}): Promise<ServiceSubscription[]> => {
  try {
    const response = await axiosInstance.get<ServiceSubscription[]>('/service-subscriptions', { 
      params: filters 
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des abonnements:', error);
    throw error;
  }
};

/**
 * Récupère un abonnement par ID
 */
export const getServiceSubscriptionById = async (id: number): Promise<ServiceSubscription> => {
  try {
    const response = await axiosInstance.get<ServiceSubscription>(`/service-subscriptions/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de la récupération de l'abonnement ${id}:`, error);
    throw error;
  }
};

/**
 * Crée un nouvel abonnement
 */
export const createServiceSubscription = async (
  subscriptionData: CreateServiceSubscriptionRequest
): Promise<ServiceSubscription> => {
  try {
    const response = await axiosInstance.post<ServiceSubscription>('/service-subscriptions', subscriptionData);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création de l\'abonnement:', error);
    throw error;
  }
};

/**
 * Met à jour un abonnement
 */
export const updateServiceSubscription = async (
  id: number, 
  subscriptionData: UpdateServiceSubscriptionRequest
): Promise<ServiceSubscription> => {
  try {
    const response = await axiosInstance.put<ServiceSubscription>(`/service-subscriptions/${id}`, subscriptionData);
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de la mise à jour de l'abonnement ${id}:`, error);
    throw error;
  }
};

/**
 * Supprime un abonnement
 */
export const deleteServiceSubscription = async (id: number): Promise<void> => {
  try {
    await axiosInstance.delete(`/service-subscriptions/${id}`);
  } catch (error) {
    console.error(`Erreur lors de la suppression de l'abonnement ${id}:`, error);
    throw error;
  }
};

/**
 * Récupère les statistiques des services par domaine
 */
export const getDomainServiceStats = async (): Promise<any[]> => {
  try {
    const response = await axiosInstance.get<any[]>('/domains/service-stats');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques:', error);
    throw error;
  }
};

/**
 * Récupère les domaines avec un abonnement Adblock LEGACY actif pour un réseau donné
 */
export const getAdblockLegacyDomains = async (networkId: number): Promise<Domain[]> => {
  try {
    const response = await axiosInstance.get<AdblockLegacyDomainsResponse>(`/domains/network/${networkId}/adblock-legacy`);
    return response.data.domains || [];
  } catch (error) {
    console.error('Erreur lors de la récupération des domaines Adblock Legacy:', error);
    throw error;
  }
};